
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox } from '@/components/ui/combobox';
import { SaveIcon, BanIcon, PlusIcon, TrashIcon, EyeIcon, DownloadIcon } from '@/components/Icons';
import { Shield, AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DocumentPreview } from '@/components/ui/DocumentPreview';
import { generatePDF } from '@/utils/pdfGenerator';
import { VerifactuCard } from '@/components/VerifactuCard';
import type { Factura, Cliente, Producto, Tax, PaymentMethod, InvoiceItem, DocumentStatus, FreelanceInfo } from '@shared/types';
import { formatearPorcentajeIrpf } from '@/utils/porcentaje';
import { importeFinitoOCero } from '@shared/importes';
import { AVISO_IMPORTES_INVALIDOS, calcularImportesDocumento } from '@/utils/importesDocumento';

// Estado inicial para una nueva factura
const createNewFactura = (numero: string, allData: any): Partial<Factura> => ({
  numero,
  clienteId: '',
  fecha: new Date().toISOString().slice(0, 10),
  fechaVencimiento: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  items: [{ id: uuidv4(), concepto: '', descripcion: '', cantidad: 1, precioUnitario: 0, taxId: allData.impuestos.find((i: Tax) => i.isDefault)?.id || allData.impuestos[0]?.id || '' }],
  estado: 'Borrador',
  aplicarIrpf: false,
});

export default function FacturaForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchWithAuth, userProfile } = useUser();
  const [factura, setFactura] = useState<Partial<Factura>>({});
  const [initialData, setInitialData] = useState<{ clientes: Cliente[]; productos: Producto[]; impuestos: Tax[]; metodosDePago: PaymentMethod[] }>({ clientes: [], productos: [], impuestos: [], metodosDePago: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<{
    existingFactura: {
      id: string;
      numero: string;
      fecha: string;
      total: number;
      estado: string;
      fechaCreacion?: string;
    };
  } | null>(null);

  // Datos importados desde imagen (si existen)
  const importedData = (location.state as any)?.importedData;

  // Detectar parámetro showPreview en la URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('showPreview') === 'true') {
      setShowPreview(true);
      // Limpiar el parámetro de la URL sin recargar
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Carga de todos los datos necesarios para el formulario
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [clientesRes, productosRes, impuestosRes, metodosRes, nextNumRes] = await Promise.all([
          fetchWithAuth('/api/clientes'),
          fetchWithAuth('/api/productos'),
          fetchWithAuth('/api/impuestos'),
          fetchWithAuth('/api/metodos-pago'),
          fetchWithAuth('/api/facturas/next-number')
        ]);

        const allData = {
          clientes: await clientesRes.json(),
          productos: await productosRes.json(),
          impuestos: await impuestosRes.json(),
          metodosDePago: await metodosRes.json(),
        };
        setInitialData(allData);

        if (id) {
          const res = await fetchWithAuth(`/api/facturas/${id}`);
          if (!res.ok) throw new Error('Factura no encontrada');
          const existingFactura = await res.json();
          setFactura(existingFactura);
        } else {
          const { nextNumber } = await nextNumRes.json();
          const newFactura = createNewFactura(nextNumber, allData);

          // Si hay datos importados desde imagen, aplicarlos
          if (importedData) {
            // Mapear items importados al formato correcto con taxId
            const mappedItems = (importedData.items || []).map((item: any) => {
              // Buscar el impuesto que coincida con el IVA extraído
              const matchingTax = allData.impuestos.find((t: Tax) =>
                Math.abs(t.rate - (item.iva || 0.21)) < 0.01
              );
              return {
                id: uuidv4(),
                concepto: item.concepto || '',
                descripcion: '',
                cantidad: item.cantidad || 1,
                precioUnitario: item.precioUnitario || 0,
                taxId: matchingTax?.id || allData.impuestos.find((t: Tax) => t.isDefault)?.id || allData.impuestos[0]?.id || '',
              };
            });

            setFactura({
              ...newFactura,
              fecha: importedData.fecha || newFactura.fecha,
              items: mappedItems.length > 0 ? mappedItems : newFactura.items,
              notas: importedData.notas || '',
              estado: 'Borrador',
              archivoOriginal: importedData.archivoOriginal, // Guardar referencia al archivo original
            });

            // Mostrar info del cliente detectado para ayudar a seleccionarlo
            if (importedData.clienteInfo?.nombre || importedData.clienteInfo?.nif) {
              toast.info('Cliente detectado en la imagen', {
                description: `${importedData.clienteInfo.nombre || ''} ${importedData.clienteInfo.nif ? `(${importedData.clienteInfo.nif})` : ''}`.trim(),
                duration: 8000,
              });
            }
          } else {
            setFactura(newFactura);
          }
        }
      } catch (error) {
        toast.error('Error al cargar datos', { description: error instanceof Error ? error.message : 'Error desconocido' });
        navigate('/facturas');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, navigate, fetchWithAuth, importedData]);

  const handleFacturaChange = useCallback((patch: Partial<Factura>) => {
    setFactura(prev => ({ ...prev, ...patch }));
  }, []);

  const updateItem = (itemId: string, patch: Partial<InvoiceItem>) => {
    const newItems = factura.items?.map(it => it.id === itemId ? { ...it, ...patch } : it);
    handleFacturaChange({ items: newItems });
  };

  const addItem = () => {
    const defaultTax = initialData.impuestos.find((i: Tax) => i.isDefault)?.id || initialData.impuestos[0]?.id || '';
    const newItem: InvoiceItem = { id: uuidv4(), concepto: '', descripcion: '', cantidad: 1, precioUnitario: 0, taxId: defaultTax };
    handleFacturaChange({ items: [...(factura.items || []), newItem] });
  };

  const removeItem = (itemId: string) => {
    handleFacturaChange({ items: factura.items?.filter(it => it.id !== itemId) });
  };

  // Cálculo en céntimos enteros (FISC-10): mismo criterio de resolución de
  // tipo que antes (impuestosMap.get(...) || 0, sin lanzar si no se
  // encuentra). La retención de IRPF sigue la misma regla que el servidor:
  // solo si "Aplicar IRPF" está marcado, con la tasa del perfil del emisor
  // (userProfile.irpfRate, 0 si no está definida), de modo que el total del
  // resumen coincide con el que se guarda (calcularTotalDocumento).
  //
  // Hallazgo de revisión R3-001: cantidad/precioUnitario se sanean con
  // importeFinitoOCero ANTES de calcularImportes, porque este useMemo se
  // ejecuta durante el render — una factura importada/cargada con un
  // importe no finito (p. ej. "1.234,56" como texto) lanzaría aquí y
  // desmontaría el formulario entero, perdiendo los cambios sin guardar.
  // Lo que se envía al guardar sigue llevando los valores originales del
  // item (ver handleSave: ...factura); el servidor valida y recalcula, y
  // es él quien debe seguir fallando fuerte ante un dato corrupto.
  //
  // En un documento emitido con instantánea (`importesEmision`) el resumen muestra
  // los importes y la tasa de IRPF guardados al emitir, no el perfil ni los
  // impuestos de hoy (misma regla de confianza que el servidor, shared/).
  const importesDoc = useMemo(() => {
    if (!factura.items) return null;
    const tasaIrpf = factura.aplicarIrpf ? (userProfile?.irpfRate ?? 0) : 0;
    const impuestosMap = new Map(initialData.impuestos.map((i: Tax) => [i.id, i.rate]));
    const lineas = factura.items.map((item) => ({
      cantidad: importeFinitoOCero(item.cantidad),
      precioUnitario: importeFinitoOCero(item.precioUnitario),
      rate: impuestosMap.get(item.taxId) || 0,
    }));
    return calcularImportesDocumento(factura, lineas, tasaIrpf);
  }, [factura, userProfile?.irpfRate, initialData.impuestos]);

  const totals = useMemo(() => {
    if (!importesDoc) return { subtotal: 0, totalImpuestos: 0, total: 0, irpf: 0 };
    return {
      subtotal: importesDoc.subtotal,
      totalImpuestos: importesDoc.iva,
      total: importesDoc.total,
      irpf: importesDoc.irpf,
    };
  }, [importesDoc]);

  // Constante para el aviso legal de IVA Exento
  const IVA_EXENTO_NOTICE = "Concepto exento de IVA - Art. 20. Uno. 26º (Ley 37/1992)";

  // Detectar si algún item tiene IVA Exento y añadir aviso automáticamente
  useEffect(() => {
    if (!factura.items || initialData.impuestos.length === 0) return;

    // Verificar si algún item tiene impuesto exento (rate = 0 o nombre contiene "exento")
    const hasExentoTax = factura.items.some(item => {
      const tax = initialData.impuestos.find((t: Tax) => t.id === item.taxId);
      if (!tax) return false;
      return tax.rate === 0 || tax.nombre.toLowerCase().includes('exento');
    });

    const currentNotas = factura.notas || '';
    const hasExentoNotice = currentNotas.includes(IVA_EXENTO_NOTICE);

    // Si tiene IVA exento y no tiene el aviso, añadirlo
    if (hasExentoTax && !hasExentoNotice) {
      const newNotas = currentNotas
        ? `${currentNotas}\n\n${IVA_EXENTO_NOTICE}`
        : IVA_EXENTO_NOTICE;
      handleFacturaChange({ notas: newNotas });
    }
  }, [factura.items, initialData.impuestos, factura.notas, handleFacturaChange]);

  const handleSave = async (skipDuplicateCheck = false) => {
    if (!factura.clienteId) {
      toast.warning('Validación', { description: 'Debes seleccionar un cliente.' });
      return;
    }

    setIsSaving(true);
    try {
      // El importe de IRPF solo sirve al resumen: no viaja en el cuerpo (el
      // servidor recalcula los totales con la tasa del perfil).
      const { irpf: _irpf, ...totalesGuardados } = totals;
      const finalFactura = { ...factura, ...totalesGuardados, skipDuplicateCheck };
      const response = await fetchWithAuth(id ? `/api/facturas/${id}` : '/api/facturas', {
        method: id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalFactura),
      });

      // Manejar respuesta de duplicado detectado (409)
      if (response.status === 409) {
        const duplicateResponse = await response.json();
        if (duplicateResponse.isDuplicate) {
          setDuplicateInfo({
            existingFactura: duplicateResponse.existingFactura,
          });
          return; // No continuar, mostrar diálogo
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al guardar');
      }

      toast.success(`Factura ${id ? 'actualizada' : 'creada'} con éxito`);
      setDuplicateInfo(null);
      navigate('/facturas');
    } catch (error) {
      toast.error('Error al guardar', { description: error instanceof Error ? error.message : 'Error desconocido' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicateConfirm = () => {
    // Proceder a guardar ignorando el duplicado
    handleSave(true);
  };

  const handleDuplicateCancel = () => {
    setDuplicateInfo(null);
    toast.info('Guardado cancelado. La factura no se ha creado.');
  };

  const handleSignWithCertificate = async () => {
    if (!id) {
      toast.warning('Aviso', { description: 'Debes guardar la factura antes de firmarla.' });
      return;
    }

    setIsSigning(true);
    try {
      const response = await fetchWithAuth(`/api/facturas/${id}/sign`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al firmar la factura');
      }

      const data = await response.json();

      // Descargar el XML firmado
      const blob = new Blob([data.signedXml], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura-${factura.numero}-firmada.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Factura firmada correctamente', {
        description: `Firmada con: ${data.certificateName}`
      });
    } catch (error) {
      toast.error('Error al firmar factura', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
    } finally {
      setIsSigning(false);
    }
  };
  
  if (isLoading) return <div>Cargando...</div>;

  const selectedCliente = initialData.clientes.find((c: Cliente) => c.id === factura.clienteId);
  const selectedPaymentMethod = initialData.metodosDePago.find((m: PaymentMethod) => m.id === factura.paymentMethodId);

  // 🔒 PROTECCIÓN VERIFACTU: Facturas emitidas con Verifactu son INALTERABLES
  const isVerifactuLocked = factura.estado !== 'Borrador' && !!factura.verifactu;

  return (
    <div className="space-y-8">
      {isVerifactuLocked && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900">Factura Protegida por VeriFactu</h3>
                <p className="text-sm text-yellow-800 mt-1">
                  Esta factura ha sido registrada con datos de integridad VeriFactu (Nº Registro: {factura.verifactu?.numeroRegistro}) y forma parte de la cadena criptográfica.
                  <br />
                  <strong>Los datos no pueden ser modificados</strong> según la normativa fiscal española.
                  Si hay un error, debes anularla y crear una factura rectificativa.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tighter">{id ? 'Editar Factura' : 'Nueva Factura'}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/facturas')} disabled={isSaving || isSigning}><BanIcon className="mr-2" />Cancelar</Button>
          <Button variant="outline" onClick={() => setShowPreview(true)} disabled={!factura.clienteId}><EyeIcon className="mr-2" />Vista Previa</Button>
          {id && factura.estado !== 'Borrador' && !factura.signedXml && (
            <Button variant="outline" onClick={handleSignWithCertificate} disabled={isSigning}>
              {isSigning ? 'Firmando...' : <><Shield className="mr-2 h-4 w-4" />Registrar con Certificado</>}
            </Button>
          )}
          {!isVerifactuLocked && (
            <Button onClick={() => handleSave()} disabled={isSaving || isSigning}>{isSaving ? 'Guardando...' : <><SaveIcon className="mr-2" />Guardar</>}</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <Card>
            <CardHeader><CardTitle>Información General</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={factura.clienteId} onValueChange={val => handleFacturaChange({ clienteId: val })} disabled={isVerifactuLocked}>
                    <SelectTrigger disabled={isVerifactuLocked}><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                    <SelectContent>{initialData.clientes.map((c: Cliente) => <SelectItem key={c.id || ''} value={c.id || ''}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nº Factura</Label>
                <Input value={factura.numero || ''} onChange={e => handleFacturaChange({ numero: e.target.value })} disabled={isVerifactuLocked} />
              </div>
              <div className="space-y-2">
                <Label>Fecha Emisión</Label>
                <Input type="date" value={factura.fecha || ''} onChange={e => handleFacturaChange({ fecha: e.target.value })} disabled={isVerifactuLocked} />
              </div>
              <div className="space-y-2">
                <Label>Fecha Vencimiento</Label>
                <Input type="date" value={factura.fechaVencimiento || ''} onChange={e => handleFacturaChange({ fechaVencimiento: e.target.value })} disabled={isVerifactuLocked} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Conceptos</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {factura.items?.map((item, index) => (
                <div key={item.id} className="p-4 border rounded-lg space-y-3">
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-3 space-y-2">
                            <Label>Concepto</Label>
                            <Input value={item.concepto} onChange={e => updateItem(item.id, { concepto: e.target.value })} placeholder="Servicio o producto" disabled={isVerifactuLocked} />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Impuesto</Label>
                            <Select value={item.taxId} onValueChange={val => updateItem(item.id, { taxId: val })} disabled={isVerifactuLocked}>
                                <SelectTrigger disabled={isVerifactuLocked}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {initialData.impuestos.map((t: Tax) => (
                                        <SelectItem key={t.id} value={t.id}>{t.nombre} ({(t.rate * 100).toFixed(0)}%)</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Cantidad</Label>
                            <Input type="number" value={item.cantidad} onChange={e => updateItem(item.id, { cantidad: +e.target.value })} disabled={isVerifactuLocked} />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Precio Unit.</Label>
                            <Input type="number" step="0.01" value={item.precioUnitario} onChange={e => updateItem(item.id, { precioUnitario: +e.target.value })} disabled={isVerifactuLocked} />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Total</Label>
                            <p className="h-10 flex items-center font-mono font-semibold">{(item.cantidad * item.precioUnitario).toFixed(2)} €</p>
                        </div>
                        <div className="col-span-1 flex items-end">
                            <Button variant="ghost" size="icon" onClick={() => removeItem(item.id)} disabled={isVerifactuLocked}><TrashIcon className="w-4 h-4 text-destructive" /></Button>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Descripción (Opcional)</Label>
                        <Textarea rows={2} value={item.descripcion || ''} onChange={e => updateItem(item.id, { descripcion: e.target.value })} placeholder="Detalles adicionales del concepto" disabled={isVerifactuLocked} />
                    </div>
                </div>
              ))}
              <Button onClick={addItem} variant="outline" disabled={isVerifactuLocked}><PlusIcon className="mr-2" /> Añadir Concepto</Button>
            </CardContent>
          </Card>

          {/* Información Verifactu */}
          <VerifactuCard
            verifactu={factura.verifactu}
            facturaNumero={factura.numero || ''}
            estado={factura.estado || 'Borrador'}
          />
        </div>
        <div className="lg:sticky lg:top-24 space-y-8">
          <Card>
            <CardHeader><CardTitle>Resumen</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {importesDoc?.fuente === 'instantanea-invalida' && (
                <Alert variant="destructive"><AlertDescription>{AVISO_IMPORTES_INVALIDOS}</AlertDescription></Alert>
              )}
              <div className="flex justify-between"><span>Subtotal</span><span>{totals.subtotal.toFixed(2)} €</span></div>
              <div className="flex justify-between"><span>Impuestos</span><span>{totals.totalImpuestos.toFixed(2)} €</span></div>
              {(importesDoc?.mostrarIrpf ?? factura.aplicarIrpf) && <div className="flex justify-between"><span>IRPF ({formatearPorcentajeIrpf(importesDoc?.irpfRateEmision ?? userProfile?.irpfRate)}%)</span><span className="text-destructive">-{totals.irpf.toFixed(2)} €</span></div>}
              <div className="flex justify-between font-bold text-xl"><span>Total</span><span>{totals.total.toFixed(2)} €</span></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Opciones</CardTitle></CardHeader>
            <CardContent className="space-y-4">
                <div className="space-y-2">
                    <Label>Estado</Label>
                    <Select value={factura.estado} onValueChange={val => handleFacturaChange({ estado: val as DocumentStatus })} disabled={isVerifactuLocked}>
                        <SelectTrigger disabled={isVerifactuLocked}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Borrador">Borrador</SelectItem>
                            <SelectItem value="Emitida">Emitida</SelectItem>
                            <SelectItem value="Pagada">Pagada</SelectItem>
                            <SelectItem value="Vencida">Vencida</SelectItem>
                            <SelectItem value="Anulada">Anulada</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center space-x-2">
                    <Checkbox id="irpf" checked={factura.aplicarIrpf} onCheckedChange={val => handleFacturaChange({ aplicarIrpf: !!val })} disabled={isVerifactuLocked} />
                    <Label htmlFor="irpf">Aplicar IRPF</Label>
                </div>
                 <div className="space-y-2">
                    <Label>Método de Pago</Label>
                    <Select value={factura.paymentMethodId} onValueChange={val => handleFacturaChange({ paymentMethodId: val })} disabled={isVerifactuLocked}>
                        <SelectTrigger disabled={isVerifactuLocked}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent>{initialData.metodosDePago.map((m: PaymentMethod) => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
            </CardContent>
          </Card>
           {selectedCliente?.esAdministracionPublica && (
             <Card>
                <CardHeader><CardTitle>Datos para FACe</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                        Rellénalos antes de emitir la factura: una vez emitida, no se pueden modificar.
                    </p>
                    <div className="space-y-2">
                        <Label htmlFor="organoProponente">Órgano proponente (DIR3)</Label>
                        <Input
                            id="organoProponente"
                            maxLength={10}
                            value={factura.organoProponente || ''}
                            onChange={e => handleFacturaChange({ organoProponente: e.target.value })}
                            disabled={isVerifactuLocked}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="numeroExpediente">Nº de expediente</Label>
                        <Input
                            id="numeroExpediente"
                            maxLength={20}
                            value={factura.numeroExpediente || ''}
                            onChange={e => handleFacturaChange({ numeroExpediente: e.target.value })}
                            disabled={isVerifactuLocked}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="referenciaPedido">Nº de propuesta / pedido</Label>
                        <Input
                            id="referenciaPedido"
                            maxLength={20}
                            value={factura.referenciaPedido || ''}
                            onChange={e => handleFacturaChange({ referenciaPedido: e.target.value })}
                            disabled={isVerifactuLocked}
                        />
                    </div>
                </CardContent>
             </Card>
           )}
           <Card>
                <CardHeader><CardTitle>Notas</CardTitle></CardHeader>
                <CardContent>
                    <Textarea value={factura.notas || ''} onChange={e => handleFacturaChange({ notas: e.target.value })} disabled={isVerifactuLocked} />
                </CardContent>
            </Card>

            {/* Documento Original (si existe) */}
            {factura.archivoOriginal && (
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2">Documento Original</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded">
                        {factura.archivoOriginal.mimeType.includes('pdf') ? (
                          <Shield className="h-5 w-5 text-primary" />
                        ) : (
                          <Shield className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{factura.archivoOriginal.nombre}</p>
                        <p className="text-xs text-muted-foreground">
                          {(factura.archivoOriginal.tamaño / 1024).toFixed(1)} KB ·
                          Subido {new Date(factura.archivoOriginal.fechaSubida).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href={factura.archivoOriginal.url} target="_blank" rel="noopener noreferrer">
                        Ver Original
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
        </div>
      </div>

      {/* Diálogo de Vista Previa */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Vista Previa - Factura {factura.numero}</DialogTitle>
              <Button variant="outline" size="sm" onClick={() => generatePDF('document-preview-pdf', `factura-${factura.numero}.pdf`, { archivoOriginal: factura.archivoOriginal })}>
                <DownloadIcon className="mr-2 h-4 w-4" />Descargar PDF
              </Button>
            </div>
            <DialogDescription className="sr-only">
              Vista previa del documento de factura antes de imprimir o descargar en PDF
            </DialogDescription>
          </DialogHeader>
          {selectedCliente && userProfile && factura.items && (
            <DocumentPreview
              document={{
                ...factura,
                tipo: 'FACTURA',
                items: factura.items || []
              } as any}
              client={selectedCliente}
              freelance={userProfile}
              taxes={initialData.impuestos}
              paymentMethod={selectedPaymentMethod}
            />
          )}
          <div className="flex justify-center pt-4 border-t">
            <Button variant="outline" onClick={() => generatePDF('document-preview-pdf', `factura-${factura.numero}.pdf`, { archivoOriginal: factura.archivoOriginal })}>
              <DownloadIcon className="mr-2 h-4 w-4" />Descargar PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo de factura duplicada detectada */}
      <AlertDialog open={!!duplicateInfo} onOpenChange={() => !isSaving && setDuplicateInfo(null)}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Posible factura duplicada
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left">
                <p>Se ha detectado una factura similar ya registrada para este cliente:</p>
                {duplicateInfo?.existingFactura && (
                  <div className="bg-muted/50 p-3 rounded-lg space-y-1 text-sm">
                    <p><span className="font-medium">Nº Factura:</span> {duplicateInfo.existingFactura.numero}</p>
                    <p><span className="font-medium">Fecha:</span> {new Date(duplicateInfo.existingFactura.fecha).toLocaleDateString('es-ES')}</p>
                    <p><span className="font-medium">Total:</span> {duplicateInfo.existingFactura.total?.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</p>
                    <p><span className="font-medium">Estado:</span> {duplicateInfo.existingFactura.estado}</p>
                    {duplicateInfo.existingFactura.fechaCreacion && (
                      <p className="text-xs text-muted-foreground">
                        Creada el {new Date(duplicateInfo.existingFactura.fechaCreacion).toLocaleDateString('es-ES')}
                      </p>
                    )}
                  </div>
                )}
                <p className="text-amber-700 font-medium">¿Deseas crear esta factura de todas formas?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDuplicateCancel} disabled={isSaving}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDuplicateConfirm} disabled={isSaving} className="bg-amber-600 hover:bg-amber-700">
              {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : 'Crear de todas formas'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
